import { Badge, Button, ScrollArea } from '@ntconsult/aluma-react';
import { differenceInCalendarDays } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProfessionalWithAllocations } from '../types/Allocation';
import { parseApiDate } from '../utils/data';
import {
  layoutAllocations,
  monthSegments,
  type TimelineWindow,
  todayMarkerPct,
  windowPeakUtilization,
} from '../utils/timelineLayout';
import type { EditAllocationTarget } from './AllocationModal';
import { ProfessionalCapacityCell } from './ProfessionalCapacityCell';
import { TimelineBar } from './TimelineBar';
import { TimelineLegend } from './TimelineLegend';

const LANE_HEIGHT_PX = 32;
const BAR_INSET_PX = 4;
const LEFT_COL = '17rem';
const LEFT_COL_PX = 272;
/** Acima de ~6 meses não cabe na largura: vira trilho fixo com scroll horizontal interno. Decidido pela janela real, não pelo range (os filtros Início/Fim podem abri-la). */
const FIT_MAX_DAYS = 186;
const PX_PER_DAY_SCROLL = 5;
/** Largura padrão da área de lanes antes do ResizeObserver medir (evita barras sem rótulo no 1º frame em modo "fit"). */
const FALLBACK_LANE_PX = 900;

type TimelineGridProps = {
  professionals: ProfessionalWithAllocations[];
  window: TimelineWindow;
  today: string;
  onView: (target: EditAllocationTarget) => void;
};

export function TimelineGrid({
  professionals,
  window: visibleWindow,
  today,
  onView,
}: TimelineGridProps) {
  const { t, i18n } = useTranslation('alocacao');
  const segments = monthSegments(visibleWindow, i18n.language);
  const todayDate = parseApiDate(today);
  const todayPct = todayDate ? todayMarkerPct(todayDate, visibleWindow) : null;

  const windowDays = differenceInCalendarDays(visibleWindow.end, visibleWindow.start) + 1;
  const isScroll = windowDays > FIT_MAX_DAYS;
  const trackPx = isScroll ? windowDays * PX_PER_DAY_SCROLL : null;

  // Largura px da área de lanes — define quanto de texto cabe nas barras (fit: medida; scroll: o trilho fixo).
  const laneHeaderRef = useRef<HTMLDivElement>(null);
  const [measuredLaneWidth, setMeasuredLaneWidth] = useState(0);
  useEffect(() => {
    const el = laneHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setMeasuredLaneWidth(entry.contentRect.width));
    observer.observe(el);
    setMeasuredLaneWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);
  const laneAreaPx = trackPx ?? (measuredLaneWidth || FALLBACK_LANE_PX);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToToday = () => {
    const el = scrollRef.current;
    if (!el || trackPx === null || todayPct === null) return;
    const todayX = LEFT_COL_PX + (todayPct / 100) * trackPx;
    el.scrollTo({ left: todayX - el.clientWidth / 2, behavior: 'smooth' });
  };

  const gridColumns = isScroll ? `${LEFT_COL} ${trackPx}px` : `${LEFT_COL} minmax(0, 1fr)`;

  return (
    <div className="flex flex-col gap-4">
      {isScroll && todayPct !== null && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<CalendarClock size={14} />}
            onClick={scrollToToday}
          >
            {t('linhaDoTempo.irParaHoje')}
          </Button>
        </div>
      )}

      {/* Altura acompanha a tela (T26): com a grade rolada até o topo do <main>, ela,
          a legenda e a paginação ocupam a tela toda. 12rem = cabeçalho do app +
          legenda + paginação + espaçamentos; 36rem é o piso em telas baixas. */}
      <ScrollArea
        ref={scrollRef}
        orientation={isScroll ? 'both' : 'vertical'}
        className="max-h-[max(36rem,calc(100dvh-12rem))] rounded-lg border border-border bg-surface"
      >
        <div
          className="relative grid"
          style={{
            gridTemplateColumns: gridColumns,
            width: isScroll && trackPx !== null ? `calc(${LEFT_COL} + ${trackPx}px)` : undefined,
          }}
        >
          {/* Fundo (atrás das barras): zebra por mês + linhas de grade alinhadas ao calendário. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-0"
            style={{ left: LEFT_COL, right: 0 }}
          >
            {segments.map((segment, index) => (
              <div
                key={segment.key}
                className={`absolute inset-y-0 border-border border-l ${
                  index % 2 === 1 ? 'bg-surface-raised' : ''
                }`}
                style={{ left: `${segment.leftPct}%`, width: `${segment.widthPct}%` }}
              />
            ))}
          </div>

          {/* Marcador de hoje (acima das barras, abaixo do cabeçalho fixo). */}
          {todayPct !== null && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 z-20"
              style={{ left: LEFT_COL, right: 0 }}
            >
              <div className="absolute inset-y-0 w-0.5 bg-info" style={{ left: `${todayPct}%` }} />
            </div>
          )}

          <div className="sticky top-0 left-0 z-30 h-9 border-border-strong border-b bg-surface" />
          <div
            ref={laneHeaderRef}
            className="sticky top-0 z-20 h-9 border-border-strong border-b bg-surface"
          >
            <div className="relative h-full">
              {segments.map((segment) => (
                <div
                  key={segment.key}
                  className="absolute inset-y-0 flex flex-col items-center justify-center px-1 text-center"
                  style={{ left: `${segment.leftPct}%`, width: `${segment.widthPct}%` }}
                  title={segment.label}
                >
                  <span className="truncate text-caption text-foreground-muted capitalize">
                    {segment.shortLabel}
                  </span>
                  {segment.firstOfYear && (
                    <span className="text-2xs text-foreground-subtle tabular-nums">
                      {segment.year}
                    </span>
                  )}
                </div>
              ))}
              {todayPct !== null && (
                <div
                  className="-translate-y-1/2 -translate-x-1/2 absolute top-1/2 z-10"
                  style={{ left: `${todayPct}%` }}
                >
                  <Badge variant="solid" tone="info" size="sm">
                    {t('linhaDoTempo.hoje')}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Linhas — uma dupla de células (capacidade + lanes) por profissional. */}
          {professionals.map((professional) => {
            const bars = layoutAllocations(professional.allocations, visibleWindow);
            const peak = windowPeakUtilization(professional.allocations, visibleWindow);
            const laneCount = Math.max(1, ...bars.map((bar) => bar.lane + 1));
            const rowHeight = laneCount * LANE_HEIGHT_PX;

            return (
              <Fragment key={professional.professionalId}>
                <ProfessionalCapacityCell
                  professional={professional}
                  peak={peak}
                  sticky={isScroll}
                />
                <div
                  className="relative z-10 border-border border-t"
                  style={{ minHeight: rowHeight }}
                >
                  {bars.map((bar) => (
                    <TimelineBar
                      key={bar.allocation.id}
                      allocation={bar.allocation}
                      leftPct={bar.leftPct}
                      widthPct={bar.widthPct}
                      top={bar.lane * LANE_HEIGHT_PX + BAR_INSET_PX}
                      height={LANE_HEIGHT_PX - BAR_INSET_PX * 2}
                      barPx={(bar.widthPct / 100) * laneAreaPx}
                      onOpen={() =>
                        onView({
                          allocation: bar.allocation,
                          professional: {
                            id: professional.professionalId,
                            registrationNumber: professional.registrationNumber,
                            name: professional.name,
                          },
                        })
                      }
                    />
                  ))}
                </div>
              </Fragment>
            );
          })}
        </div>
      </ScrollArea>

      <TimelineLegend />
    </div>
  );
}
